import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination"
import { useTranslation } from "react-i18next";

import FeatureLabel from "../components/FeatureLabel";
import VideoPlayer from "../components/VideoPlayer";
import FeatureCarousel from "../components/FeatureCarousel";
import { Link } from "react-router-dom";


gsap.registerPlugin(ScrollTrigger);

function Home() {
	const { t } = useTranslation();

	const leftPhoneRef = useRef(null);
	const rightPhoneRef = useRef(null);

	useEffect(() => {
		// Video Zoom In/Out Effect
		gsap.fromTo(
			".video-section",
			{ scale: 1 },
			{
				scale: 1.1,
				scrollTrigger: {
					trigger: ".video-section",
					start: "top center",
					end: "bottom center",
					scrub: true,
				},
			}
		);

		// Text Slide-in Effect
		gsap.fromTo(
			".text-section",
			{ x: -100, opacity: 0 },
			{
				x: 0,
				opacity: 1,
				scrollTrigger: {
					trigger: ".text-section",
					start: "top 70%", // Starts when the top of the element is at 80% of the viewport height
					end: "middle center",
					scrub: 3,
				},
			}
		);
	}, []);


	useLayoutEffect(() => {
		if (leftPhoneRef.current && rightPhoneRef.current) {
			gsap.to(leftPhoneRef.current, {
				rotation: 0,
				x: -40,
				scrollTrigger: {
					trigger: document.body,
					start: "top top",
					end: "+=200",
					scrub: 1,
				},
			});

			gsap.to(rightPhoneRef.current, {
				rotation: 2,
				x: 100,
				scrollTrigger: {
					trigger: document.body,
					start: "top top",
					end: "+=200",
					scrub: 1,
				},
			});
		}
	}, []);

	useEffect(() => {
		// Generic fade-in-up for all elements with .fade-in-up
		gsap.utils.toArray(".fade-in-up").forEach((el) => {
			gsap.fromTo(
				el,
				{ y: 50, opacity: 0 },   // Start: slightly lower + invisible
				{
					y: 0,
					opacity: 1,
					duration: 1,
					ease: "power2.out",
					scrollTrigger: {
						trigger: el,
						start: "top 85%",   // When element enters viewport
						toggleActions: "play none none reverse", // Animate in/out
					},
				}
			);
		});
	}, []);

	const featureCards = [
		{ bg: "#9B29FF", img: "icon1.png", header: t('home.card1Header'), text: t('home.card1Text') },
		{ bg: "#671BFF", img: "icon2.png", header: t('home.card2Header'), text: t('home.card2Text') },
		{ bg: "#9B29FF", img: "icon1.png", header: t('home.card3Header'), text: t('home.card3Text') },
		{ bg: "#671BFF", img: "icon3.png", header: t('home.card4Header'), text: t('home.card4Text') },
	];

	const featureStrips = [
		t('home.feature2_1'),
		t('home.feature2_2'),
		t('home.feature2_3'),
		t('home.feature2_4'),
	];

	return (
		<>

			<div className="bg-background-gradient rounded-3xl lg:min-h-[65vh] pb-28 lg:pb-0 relative">

					{/* Background image */}
					<div
						className="absolute inset-0 bg-no-repeat bg-center bg-cover rounded-3xl"
						style={{ backgroundImage: `url(${require("../media/images/MainSectionStars.png")})` }}
					/>

					{/* Blur layer with fade */}
					<div
						className="absolute inset-0 backdrop-blur-md rounded-3xl"
						style={{
							maskImage: "linear-gradient(to right, rgba(0,0,0,0.2) 0%, black 7%, black 30%, rgba(0,0,0,0) 100%)",
							WebkitMaskImage: "linear-gradient(to right, rgba(0,0,0,0.2) 0%, black 7%, black 30%, rgba(0,0,0,0) 100%)",
						}}
					/>

					{/* Content */}
					<div className="relative flex flex-col lg:flex-row mx-8 sm:mx-0">
					{/* Left side: text */}
					<div className="lg:min-h-[65vh] lg:w-[40%] xl:w-[50%] flex flex-col items-start ml-responsive mr-responsive lg:mr-0 lg:pr-24">
						<div className="mb-5 mt-12 lg:mt-24">
							<img
								className="transition-all duration-1000 ease-in-out delay-100"
								src={require("../media/images/star.png")}
								alt="Star"
							/>
						</div>

						<div className="transition-all duration-1000 ease-in-out delay-300 mb-8 lg:mb-12">
							<h1 className="text-4xl sm:text-4xl md:text-6xl lg:text-4xl xl:text-6xl leading-[1.1]">
								{t('home.heroHeadline')}
							</h1>
						</div>

						<p className="text-base text-white mb-2 font-light">
							{t('home.heroSubtitle')}
						</p>

						<h2 className="text-2xl sm:text-3xl lg:text-2xl xl:text-4xl text-white mb-6">
							{t('home.heroTagline')}
						</h2>

						<div className="transition-all duration-1000 ease-in-out delay-500 mb-16 lg:mb-24 z-20">
							<Link
								to="/demo"
								className="relative px-6 py-2 text-[--primary-light] text-base font-medium tracking-wide rounded-full border-2 border-white
										transition-all duration-300 ease-in-out hover:bg-white hover:text-[#9B29FF]
										"
							>
								{t('home.heroButton')}
							</Link>
						</div>
					</div>

					{/* Right side: phones (only visible on large screens) */}
					<div className="hidden lg:block w-[60%] xl:w-[50%] relative">
						<img
							ref={leftPhoneRef}
							className="h-[700px] absolute -translate-x-12 scale-90 object-contain z-20 rotate-12"
							src={require("../media/images/phone-1.png")}
							alt="Phone 1"
						/>
						<img
							ref={rightPhoneRef}
							className="h-[700px] absolute translate-x-16 translate-y-20 scale-90 object-contain z-10 rotate-12"
							src={require("../media/images/phone-2.png")}
							alt="Phone 2"
						/>
					</div>

				</div>
			</div>
			<div className="lg:hidden relative flex justify-center w-full">
				<img
					className="h-[700px] absolute w-full -translate-y-32 -translate-x-4 object-contain z-20"
					src={require("../media/images/phone-3.png")}
					alt="Phone 1"
				/>
			</div>



			<div className="ml-responsive mr-responsive lg:w-[45%] mt-[640px] lg:mt-36">
				<h2 className="text-black text-4xl mb-2 fade-in-up"><span className="text-[#9B29FF]">{t('home.scenarioHighlight')}</span>{t('home.scenarioRest')}</h2>
				<p className="fade-in-up">{t('home.scenarioBody')}</p>
			</div>

			<div className="hidden sm:grid grid-cols-1 md:grid-cols-4 ml-responsive mt-12 mb-12 gap-y-8 md:gap-y-0 md:divide-x md:divide-[#9B29FF]">
				<div className="flex flex-col items-start pr-6">
					<h2 className="text-black text-2xl mb-2 fade-in-up">{t('home.skill1Title')}</h2>
					<p className="text-base fade-in-up">
						{t('home.skill1Text')}
					</p>
				</div>

				<div className="flex flex-col items-start pl-0 md:px-6">
					<h2 className="text-black text-2xl mb-2 fade-in-up">{t('home.skill2Title')}</h2>
					<p className="text-base fade-in-up">
						{t('home.skill2Text')}
					</p>
				</div>

				<div className="flex flex-col items-startpl-0 md:px-6">
					<h2 className="text-black text-2xl mb-2 fade-in-up">{t('home.skill3Title')}</h2>
					<p className="text-base fade-in-up">
						{t('home.skill3Text')}
					</p>
				</div>

				<div className="flex flex-col items-start pl-0 md:pl-6">
					<h2 className="text-black text-2xl mb-2 fade-in-up">{t('home.skill4Title')}</h2>
					<p className="text-base fade-in-up">
						{t('home.skill4Text')}
					</p>
				</div>
			</div>

			<FeatureCarousel />


			<div className="relative w-full flex flex-col items-center justify-center px-0 md:px-16 my-48 lg:my-48">
				<img
					src={require("../media/images/bg-animation.png")}
					alt="Background Animation"
					className="absolute inset-0 w-full h-full scale-[135%] top-14
							object-contain z-0 pointer-events-none select-none"
					style={{ objectPosition: 'center' }}
				/>
				<div className="max-w-screen-lg w-full px-4 md:px-0">

					<div
						className="relative flex justify-center items-center w-full video-section aspect-w-16 aspect-h-9 max-w-screen-xl rounded-3xl overflow-hidden"
						style={{
							boxShadow: '0px 0px 0px 1px #00000008, 0px 10px 72px 5px #32325D6B'
						}}
					>
						<VideoPlayer />
					</div>
				</div>
			</div>

			<div className="relative">

				<div className="absolute inset-0 z-10 w-screen left-1/2 right-1/2 -mx-[50vw]">
					<video
						className="w-full h-[300px] sm:h-[400px] lg:h-[600px] object-cover"
						src={require("../media/videos/video-call-and-computer-in-office.mov")}
						autoPlay
						muted
						loop
					/>
				</div>

				<div className="h-[150px] sm:h-[220px] lg:h-[420px]" />


				<div className="relative bg-white rounded-3xl py-12 md:py-32 z-20 mt-20">
					<div className="ml-responsive mr-responsive px-12 sm:px-0">

						<div className="fade-in-up">
							<FeatureLabel text={t('home.featuresLabel')} />
						</div>

						<h2 className="text-black text-4xl mb-6 fade-in-up">{t('home.featuresHeading')}</h2>

						<div className="flex flex-col justify-center relative">
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 z-10 mx-auto w-full fade-in-up">
								{featureCards.map((card, index) => (
									<div
										key={index}
										className="p-6 sm:p-8 rounded-xl flex-1 shadow leading-7 relative overflow-hidden
												transition-all duration-300 ease-in-out
												hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
										style={{ backgroundColor: card.bg }}
									>
										{/* Gradient Overlay Effect */}
										<div className="absolute inset-0 bg-white opacity-0 transition-opacity duration-300 hover:opacity-10"></div>

										<img className="mb-4 relative z-10 w-10 sm:w-auto" src={require(`../media/images/${card.img}`)} alt={card.header} />
										<h3 className="text-3xl mb-4">{card.header}</h3>
										<p className="text-white text-base relative z-10">{card.text}</p>
									</div>
								))}
							</div>
						</div>

					</div>
				</div>
			</div>

			<div className="justify-center relative mt-12 mb-12 md:-mt-28 md:mb-0 hidden md:flex">
				<img className="fade-in-up z-20" src={require("../media/images/Eloquent-i_ipad.gif")}
					alt="iPad device with Eloquent-i app" />
			</div>

			<div className="text-center mb-16 max-w-screen-md mx-auto mt-16 md:mt-16">
				<h2 className="text-4xl md:text-7xl text-black fade-in-up">{t('home.infiniteHeading')} <br />
					<span className="text-[#6712FF] text-7xl md:text-9xl pt-4 block fade-in-up">{t('home.infiniteHighlight')}</span>
				</h2>
			</div>

			<div className="block md:hidden relative -mx-12">
				<img
					className="w-full h-auto object-cover object-left"
					src={require("../media/images/ipad2.png")}
					alt="iPad device with Eloquent-i app (mobile)"
				/>
			</div>

			<div className="bg-white rounded-3xl py-12 md:py-32 mb-12 -mt-32 md:mt-0">
				<div className="ml-responsive mr-responsive flex relative px-12 md:px-0">
					<div className="w-full md:w-[55%]">
						<div className="fade-in-up">
							<FeatureLabel text={t('home.aiLabel')} />
						</div>
						<div className="sm:pr-12">
							<p className="text-black text-lg sm:text-xl mb-10 font-normal fade-in-up">
								{t('home.aiTagline')}
							</p>

							<p className="fade-in-up text-sm sm:text-base">{t('home.aiBody')}</p>
						</div>
					</div>
					<div className="w-[45%] relative hidden md:block z-30">
						<img
							className="absolute w-full xl:-top-20 right-0 fade-in-up"
							src={require("../media/images/person1.png")}></img>
					</div>
				</div>
			</div>


			<div className="relative block sm:hidden w-screen h-[600px] scale-110 overflow-hidden left-1/2 -translate-x-1/2">
				<img
					className="absolute inset-0 w-full h-full object-contain transform"
					src={require("../media/images/person1.png")}
					alt="Person using Eloquent-i app"
				/>
			</div>

			<div className="relative bg-white rounded-3xl py-12 md:py-32 z-20">
				<div className="ml-responsive mr-responsive px-12 md:px-0">
					<div className="fade-in-up">
						<FeatureLabel text={t('home.feedbackLabel')} />
					</div>
					<p className="mb-16 fade-in-up">{t('home.feedbackIntro')}</p>



					<div className="flex flex-col md:flex-row gap-12">
						<div className="w-full md:w-1/2">
							<h2 className="text-black text-2xl mb-2 fade-in-up">{t('home.measureTitle')}</h2>
							<p className="mb-8 fade-in-up text-sm sm:text-base">{t('home.measureText')}</p>
						</div>

						<div className="w-full md:w-1/2">
							<h2 className="text-black text-2xl mb-2 fade-in-up">{t('home.bigPictureTitle')}</h2>
							<p className="mb-8 fade-in-up text-sm sm:text-base">{t('home.bigPictureText')}</p>
						</div>
					</div>


				</div>
			</div>



			<div className="relative -mt-20 w-screen h-96 md:h-[500px] lg:h-[600px] xl:h-[700px] 2xl:h-[800px] mb-12 overflow-hidden left-1/2 -translate-x-1/2">
				<img
					className="absolute inset-0 w-full h-full object-cover transform object-bottom"
					src={require("../media/images/Analytics.png")}
					alt="Analytics dashboard screenshot"
				/>
			</div>


			<div className="text-center mb-16 max-w-screen-md mx-auto">
				<h2 className="text-5xl sm:text-7xl text-black mb-8 fade-in-up">
					{t('home.fastFlexibleHeading')}
				</h2>
				<p className="text-base fade-in-up">
					{t('home.fastFlexibleBody')}
				</p>
			</div>

			<div className="flex-col md:flex-row justify-center mb-20 relative hidden md:flex">
				<div className="flex bg-background-gradient justify-around items-center px-12 py-14
						border border-white border-solid rounded-3xl mx-auto w-full fade-in-up ">
					<p className="p-4 font-iBrand text-white text-2xl flex-1 text-center leading-7">
						{t('home.feature2_1')}
					</p>

					<img
						src={require("../media/images/Line.png")}
						alt="Section divider line"
						className="mx-4"
					/>

					<p className="p-4 font-iBrand text-white text-2xl flex-1 text-center">
						{t('home.feature2_2')}
					</p>

					<img
						src={require("../media/images/Line.png")}
						alt="Divider"
						className="mx-4"
					/>

					<p className="p-4 font-iBrand text-white text-2xl flex-1 text-center">
						{t('home.feature2_3')}
					</p>

					<img
						src={require("../media/images/Line.png")}
						alt="Divider"
						className="mx-4"
					/>

					<p className="p-4 font-iBrand text-white text-2xl flex-1 text-center">
						{t('home.feature2_4')}
					</p>
				</div>
			</div>

			<div className="md:hidden mb-20 md-hidden-swiper">
				<div className="bg-background-gradient h-48 rounded-[3xl] relative px-6">

					<Swiper
						modules={[Pagination]}
						spaceBetween={10}
						slidesPerView={1}
						pagination={{
							el: '.swiper-pagination', // explicitly attach pagination to the div outside
							clickable: true
						}}
						className="h-full"
					>
						{featureStrips.map((text, index) => (
							<SwiperSlide key={index}>
								<div className="flex items-center justify-center relative h-full">

									{/* Left Divider */}
									<img
										src={require("../media/images/Line.png")}
										alt="Section divider line"
										className="absolute left-4"
									/>

									{/* Text */}
									<p className="font-iBrand text-white text-xl text-center flex-1 mx-12">
										{text}
									</p>

									{/* Right Divider */}
									<img
										src={require("../media/images/Line.png")}
										alt="Section divider line"
										className="absolute right-4"
									/>
								</div>
							</SwiperSlide>
						))}
					</Swiper>
				</div>
				{/* Pagination below */}
				<div className="swiper-pagination mt-4"></div>
			</div>

		</>
	);
}

export default Home;
